"use client";

import { useState, useRef, useEffect } from "react";
import { IoCloudUploadOutline, IoClose } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  createChecklist,
  updateChecklist,
} from "../../../redux/features/checklistSlice";
import { forceLogout } from "../../../utlis/forceLogout";

const DocumentUploadForm = ({
  selectedCategory,
  selectedCountry,
  setSelectedCategory,
  countries,
  visaCategories,
  onVisaTypesUpdate,
}) => {
  const [documentName, setDocumentName] = useState("");
  const [email, setEmail] = useState("");
  const [fees, setFees] = useState("");
  const [file, setFile] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [existingDocument, setExistingDocument] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const findMatchingVisaType = () => {
    const target = String(selectedCategory || "").trim().toLowerCase();
    return (visaCategories || []).find((vt) => {
      const candidates = [vt?._id, vt?.value, vt?.title, vt?.label]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
      return candidates.includes(target);
    });
  };

  useEffect(() => {
    setSuccessMessage("");
    setValidationError("");
    setError(null);

    return () => {
      setSuccessMessage("");
      setValidationError("");
      setError(null);
    };
  }, []);

  useEffect(() => {
    setSuccessMessage("");
    setValidationError("");
    setError(null);

    if (selectedCategory && visaCategories && visaCategories.length > 0) {
      // Find the visa type matching the selected title
      const matchingVisaType = findMatchingVisaType();
      
      console.log("Matching Visa Type:", matchingVisaType);

      if (matchingVisaType) {
        setIsUpdate(true);
        // Use checklist document name for the editable document-name input.
        setDocumentName(matchingVisaType.checkListDocumentName || "");
        setEmail(matchingVisaType.customerserviceemail || ""); 
        setFees(matchingVisaType.fees?.toString() || "");
        // Handle checklist document URL string
        setExistingDocument(
          matchingVisaType.checkListDocument 
          ? {
            location: matchingVisaType.checkListDocument,
            name: matchingVisaType.checkListDocumentName || matchingVisaType.title || "",
            } 
            : null
        );
      } else {
        setIsUpdate(false);
        setDocumentName("");
        setEmail("");
        setFees("");
        setExistingDocument(null);
        setFile(null);
      }
    }
  }, [selectedCategory, visaCategories]);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (
      droppedFile &&
      (droppedFile.type === "application/pdf" ||
        droppedFile.type === "application/msword" ||
        droppedFile.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    ) {
      if (droppedFile.size <= 5 * 1024 * 1024) {
        setFile(droppedFile);
        setExistingDocument(null);
      } else {
        alert("File size must be less than 5MB");
      }
    } else {
      alert("Please upload a PDF or Word document");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.type === "application/pdf" ||
        selectedFile.type === "application/msword" ||
        selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    ) {
      if (selectedFile.size <= 5 * 1024 * 1024) {
        setFile(selectedFile);
        setExistingDocument(null);
      } else {
        alert("File size must be less than 5MB");
      }
    } else {
      alert("Please upload a PDF or Word document");
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setExistingDocument(null);
  };

  const handleUpload = async () => {
    setValidationError("");
    setSuccessMessage("");
    setError(null);

    // Basic validation
    // documentName might be pre-filled with title, email and fees might be empty initially
    if (!documentName || !email || !selectedCategory) {
      setValidationError("Please fill in required fields.");
      return;
    }
    
    // Find the visa type to get the ID
    const matchingVisaType = findMatchingVisaType();

    if (!matchingVisaType) {
      setError("Visa type not found.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    // Send document name using the documentName field key.
    formData.append("documentName", documentName);
    formData.append("customerserviceemail", email);
    formData.append("fees", fees);
    formData.append("isActive", "true");
    
    // Include file only if a new one is selected
    if (file) {
      formData.append("checkListDocument", file);
    }
    
    // categoryCode mentioned but unclear, maybe send ID or existing code if available?
    // User: "categoryCode string now id" - might mean send ID as categoryCode?
    // Or just omit if not sure. "param: visa type id" is handled in URL.   
    // Let's assume categoryCode is not mandatory or already handled by backend via ID.

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/visatype/${matchingVisaType._id}`, {
          method: 'PUT',
          headers: {
              'Authorization': `Bearer ${token}`
              // Content-Type is set automatically for FormData
          },
          body: formData
      });

      const data = await response.json();

      if (data?.code === 401) {
        forceLogout();
        return;
      }

      if (response.ok) {
        if (typeof onVisaTypesUpdate === "function") {
          onVisaTypesUpdate({
            ...matchingVisaType,
            checkListDocumentName: documentName,
            customerserviceemail: email,
            fees: Number(fees) || 0,
            checkListDocument:
              data?.body?.visaType?.checkListDocument || matchingVisaType.checkListDocument,
          });
        }
        setSuccessMessage("Visa type updated successfully!");
        // Update local state if needed (e.g., setExistingDocument with new URL from response)
        // For now just show success message
        setTimeout(() => {
          setSuccessMessage("");
        }, 4000);
      } else {
        throw new Error(data.message || "Update failed");
      }
    } catch (err) {
      console.error("Update failed:", err);
      setError(err.message || "Failed to update visa type");
    } finally {
      setLoading(false);
    }
  };

  const sanitizeNumericInput = (value) => {
    return value.replace(/\D/g, '');
  };

  const handleFeeChange = (e) => {
    const sanitized = sanitizeNumericInput(e.target.value);
    // Limit to 10 digits (max ~3.2 billion)
    if (sanitized.length <= 10) {
      setFees(sanitized);
    }
  };

  const handleDocumentNameChange = (e) => {
    // Limit to 100 characters
    const value = e.target.value;
    if (value.length <= 100) {
      setDocumentName(value);
    }
  };

  const handleEmailChange = (e) => {
    // Limit to 120 characters
    const value = e.target.value;
    if (value.length <= 120) {
      setEmail(value);
    }
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "Document";
    const parts = url.split("/");
    return parts[parts.length - 1].length > 20
      ? parts[parts.length - 1].substring(0, 20) + "..."
      : parts[parts.length - 1];
  };

  const getDisplayDocumentName = () => {
    if (existingDocument?.name) return existingDocument.name;
    return getFileNameFromUrl(existingDocument?.location);
  };

  return (
    <div className="mt-6 max-w-5xl text-sm md:text-base">
      {successMessage && (
        <AnimatePresence>
          <motion.div
            className="border-2 border-green-500 text-green-700 px-4 py-3 rounded-lg relative mb-4 bg-green-50 shadow-md"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className="flex items-center font-medium">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-green-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {successMessage}
            </span>
          </motion.div>
        </AnimatePresence>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="flex flex-col-reverse md:flex-row gap-3 md:gap-0 items-center justify-between mb-6">
        <h2 className="md:text-lg  flex flex-row">
          <span>Visa Category:</span>&nbsp;
          <span className="font-medium">{selectedCategory}</span>
        </h2>
        <div className="flex items-center space-x-2 rounded-full border border-[#87CA60]/60 bg-white px-2 py-1">
          <img
            src={
              countries.find((c) => c.name === selectedCountry)?.flag ||
              "/placeholder.svg" ||
              "/placeholder.svg"
            }
            alt={selectedCountry}
            className="h-6 md:w-8 w-6 md:h-8 rounded-full object-cover"
          />
          <span className="block text-sm md:text-base">{selectedCountry}</span>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm md:text-base mb-5">
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block">Enter Name of Document</label>
              <span className="text-xs text-gray-500">{documentName.length}/100</span>
            </div>
            <input
              type="text"
              maxLength="100"
              value={documentName}
              onChange={handleDocumentNameChange}
              placeholder="e.g Checklist for Study Visa"
              className="w-full p-2.5 border rounded-lg placeholder:text-gray-400"
            />
            <p className="text-xs text-gray-500 mt-1">Max 100 characters</p>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2 md:mt-1">
              <label className="text-sm">Enter Email Address for Customer Service</label>
              <span className="text-xs text-gray-500">{email.length}/120</span>
            </div>
            <input
              type="email"
              maxLength="120"
              value={email}
              onChange={handleEmailChange}
              className="text-sm md:text-base w-full p-2.5 border rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">Max 120 characters</p>
          </div>
        </div>

        <div className="flex flex-col-reverse md:flex-row gap-6 w-full">
          <div className="w-full">
            <label className="block text-sm mb-2">Check List Document</label>
            {!file && !existingDocument ? (
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <IoCloudUploadOutline className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm font-medium">
                  Click to upload or drag and drop
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Word Document or Pdf (MAX. 5mb)
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".doc,.docx,.pdf"
                  className="hidden"
                />
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 border rounded-lg">
                {file ? (
                  <span className="text-sm text-blue-600">{file.name}</span>
                ) : existingDocument ? (
                  <a
                    href={existingDocument.location}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {getDisplayDocumentName()}
                  </a>
                ) : null}
                <button
                  onClick={handleClearFile}
                  className="text-red-500 hover:text-red-700"
                >
                  <IoClose className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
          <div className="text-sm md:text-base w-full">
            <div className="flex justify-between items-center mb-2">
              <label className="block">Fee for Study visa</label>
              <span className="text-xs text-gray-500">{fees.length}/10</span>
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength="10"
              value={fees}
              onChange={handleFeeChange}
              placeholder="e.g 40 (numeric only)"
              className="w-full p-2.5 border rounded-lg placeholder:text-gray-400 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <p className="text-xs text-gray-500 mt-1">Numbers only, Max 10 digits</p>
          </div>
        </div>
        {validationError && (
          <p className="text-red-500 text-right text-sm mt-4">
            {validationError}
          </p>
        )}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => {
              setSelectedCategory(null);
              setValidationError("");
            }}
            className="text-sm md:text-base px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            Back
          </button>
          <button
            onClick={handleUpload}
            disabled={loading}
            className={`h-[36px] w-[83.6px] md:h-[40px] md:w-[91px] text-sm md:text-base last:bg-[#036494] text-white px-4 py-2 rounded-full hover:bg-[#025177] transition-colors flex flex-center justify-center ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <span className="loader"></span>
            ) : isUpdate ? (
              "Update"
            ) : (
              "Upload"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadForm;
